import { FREE, NO, OWN, SHIPPING, TUTULIST_LOWERCASE, YES } from '../../util/constants';
import { normalizePhoneNumber, normalizePhoneNumberToE164 } from '../../util/phoneNumber';

export const shippingDefaultDimensions = {
  length: '8.5',
  width: '11',
  height: '1',
};

const toCountryCode = country => country?.code || country?.value || country || null;

const toNullableValue = value => (value === undefined ? null : value);

const getAddressField = (address, primaryKey, fallbackKey, legacyKey) => {
  const primaryValue = address?.[primaryKey];
  if (primaryValue !== undefined && primaryValue !== null) {
    return primaryValue;
  }

  const fallbackValue = address?.[fallbackKey];
  if (fallbackValue !== undefined && fallbackValue !== null) {
    return fallbackValue;
  }

  const legacyValue = address?.[legacyKey];
  return legacyValue !== undefined && legacyValue !== null ? legacyValue : '';
};

export const normalizeShippingDetailsAddress = shippingAddress => {
  const nestedAddress =
    shippingAddress?.address && typeof shippingAddress.address === 'object'
      ? shippingAddress.address
      : null;
  const sourceAddress = nestedAddress || shippingAddress || {};

  return {
    line1: getAddressField(sourceAddress, 'line1', 'street1', 'addressLine1'),
    line2: getAddressField(sourceAddress, 'line2', 'street2', 'addressLine2'),
    city: sourceAddress?.city || '',
    state: sourceAddress?.state || sourceAddress?.region || '',
    zip: getAddressField(sourceAddress, 'zip', 'postalCode', 'postal_code'),
    country: toCountryCode(sourceAddress?.country) || 'US',
    phoneNumber: normalizePhoneNumberToE164(
      getAddressField(sourceAddress, 'phoneNumber', 'phone') ||
        getAddressField(shippingAddress, 'phoneNumber', 'phone')
    ),
  };
};

const getUserProtectedData = user => user?.attributes?.profile?.protectedData || {};

const getPhoneVerificationResetData = ({ currentUser, phoneNumber }) => {
  const protectedData = getUserProtectedData(currentUser);
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber || '');
  const currentVerifiedPhoneNumber = normalizePhoneNumber(
    protectedData.phoneNumberVerifiedValue ||
      (protectedData.phoneNumberVerified ? protectedData.phoneNumber : '')
  );
  const isAlreadyVerifiedPhone =
    Boolean(normalizedPhoneNumber) &&
    Boolean(protectedData.phoneNumberVerified) &&
    currentVerifiedPhoneNumber === normalizedPhoneNumber;

  return isAlreadyVerifiedPhone
    ? {}
    : {
        phoneNumberVerified: false,
        phoneNumberVerifiedAt: null,
      };
};

const toNumberOrNull = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedNumber = Number(value);
  return Number.isFinite(parsedNumber) ? parsedNumber : null;
};

const getThresholdAmountValue = thresholdAmount => {
  if (thresholdAmount && typeof thresholdAmount === 'object') {
    return thresholdAmount.amount ?? null;
  }

  return toNumberOrNull(thresholdAmount);
};

const getThresholdCurrency = thresholdAmount => {
  if (thresholdAmount && typeof thresholdAmount === 'object') {
    return thresholdAmount.currency || null;
  }

  return null;
};

export const buildOwnShippingProfileResetPayload = ({
  currentUser,
  enableNonUSOrders,
  phoneNumber,
} = {}) => {
  // Profile data is merged on update, so Shippo-specific keys need explicit nulls
  // when a seller moves back to own shipping.
  const protectedData = {
    shippingPreference: OWN,
    otherShippingPreferences: null,
    shippoAddress: null,
    threshold: null,
    thresholdAmount: null,
    thresholdCurrency: null,
  };

  const normalizedPhoneNumber = normalizePhoneNumberToE164(phoneNumber);

  if (phoneNumber !== undefined) {
    protectedData.phoneNumber = toNullableValue(normalizedPhoneNumber);
    Object.assign(
      protectedData,
      getPhoneVerificationResetData({
        currentUser,
        phoneNumber: normalizedPhoneNumber,
      })
    );
  }

  if (enableNonUSOrders !== undefined) {
    protectedData.enableNonUSOrders = enableNonUSOrders;
  }

  return {
    protectedData,
    publicData: {
      shippingPreference: OWN,
      addressObjectId: null,
      shippingType: null,
      thresholdAmount: null,
    },
  };
};

export const buildTutulistShippingProfilePayload = ({ currentUser, weight, massUnit } = {}) => {
  const profile = currentUser?.attributes?.profile || {};
  const protectedData = profile.protectedData || {};
  const publicData = profile.publicData || {};
  const existingOtherShippingPreferences = protectedData.otherShippingPreferences || {};
  const shippingType =
    existingOtherShippingPreferences.shippingType || publicData.shippingType || SHIPPING;
  const threshold =
    protectedData.threshold !== undefined
      ? protectedData.threshold
      : existingOtherShippingPreferences.threshold;
  // A minimum switched off stays off; the public copy is only a fallback for older accounts
  // saved before the on/off switch existed
  const thresholdAmount =
    threshold === NO
      ? null
      : protectedData.thresholdAmount !== undefined
      ? protectedData.thresholdAmount
      : existingOtherShippingPreferences.thresholdAmount ?? publicData.thresholdAmount;
  const thresholdCurrency =
    protectedData.thresholdCurrency !== undefined
      ? protectedData.thresholdCurrency
      : existingOtherShippingPreferences.thresholdCurrency;

  const otherShippingPreferences = {
    ...shippingDefaultDimensions,
    ...existingOtherShippingPreferences,
    shippingType,
  };

  if (weight !== undefined) {
    otherShippingPreferences.weight = toNullableValue(weight);
  }

  if (massUnit !== undefined) {
    otherShippingPreferences.massUnit = toNullableValue(massUnit);
  }

  const nextProtectedData = {
    shippingPreference: TUTULIST_LOWERCASE,
    otherShippingPreferences,
  };

  if (threshold !== undefined) {
    nextProtectedData.threshold = threshold;
  }

  if (thresholdAmount !== undefined) {
    nextProtectedData.thresholdAmount = thresholdAmount;
  }

  if (thresholdCurrency !== undefined) {
    nextProtectedData.thresholdCurrency = thresholdCurrency;
  }

  return {
    protectedData: nextProtectedData,
    publicData: {
      shippingPreference: TUTULIST_LOWERCASE,
      shippingType,
      // null removes an old public minimum when the seller has switched it off
      ...(thresholdAmount !== undefined ? { thresholdAmount } : {}),
    },
  };
};

export const buildShippingDetailsInitialValues = ({
  shippingAddress = {},
  otherShippingPreferences = {},
  shippingPreference,
  shippingType,
  thresholdParams = {},
  enableNonUSOrders,
  defaults = {},
} = {}) => {
  const normalizedShippingAddress = normalizeShippingDetailsAddress(shippingAddress);

  return {
    city: normalizedShippingAddress.city,
    state: normalizedShippingAddress.state,
    country: normalizedShippingAddress.country,
    line1: normalizedShippingAddress.line1,
    line2: normalizedShippingAddress.line2,
    phoneNumber: normalizedShippingAddress.phoneNumber,
    zip: normalizedShippingAddress.zip,
    ...shippingDefaultDimensions,
    ...otherShippingPreferences,
    shippingPreference: shippingPreference || TUTULIST_LOWERCASE,
    shippingType: shippingType || otherShippingPreferences?.shippingType || SHIPPING,
    threshold: thresholdParams?.threshold ?? otherShippingPreferences?.threshold ?? NO,
    thresholdAmount: thresholdParams?.thresholdAmount || null,
    enableNonUSOrders, // we are expecting an array here not string
    ...defaults,
  };
};

export const buildShippingDetailsSubmitPayload = (values, options = {}) => {
  const { currentUser } = options;
  const {
    line1,
    line2,
    city,
    state,
    zip,
    country,
    shippingType = null,
    threshold = null,
    thresholdAmount = null,
    length = null,
    width = null,
    height = null,
    weight = null,
    massUnit = null,
    shippingPreference,
    phoneNumber,
    enableNonUSOrders,
  } = values || {};
  const normalizedPhoneNumber = normalizePhoneNumberToE164(phoneNumber);

  const resolvedShippingPreference = shippingPreference || TUTULIST_LOWERCASE;
  const isFreeShipping = shippingType === FREE;
  const normalizedThresholdAmount =
    !isFreeShipping && threshold === YES ? getThresholdAmountValue(thresholdAmount) : null;
  const normalizedThresholdCurrency =
    !isFreeShipping && threshold === YES ? getThresholdCurrency(thresholdAmount) : null;

  const updatedShippingPreferences = {
    length: toNullableValue(length),
    width: toNullableValue(width),
    height: toNullableValue(height),
    weight: toNullableValue(weight),
    massUnit: toNullableValue(massUnit),
    ...(resolvedShippingPreference === TUTULIST_LOWERCASE
      ? {
          threshold: !isFreeShipping ? threshold : null,
          thresholdAmount: normalizedThresholdAmount,
          thresholdCurrency: normalizedThresholdCurrency,
          shippingType,
        }
      : {}),
  };

  const shippingDetails = {
    line1: toNullableValue(line1),
    line2: toNullableValue(line2),
    city: toNullableValue(city),
    state: toNullableValue(state),
    zip: toNullableValue(zip),
    country: toCountryCode(country),
    phoneNumber: toNullableValue(normalizedPhoneNumber),
    enableNonUSOrders: values?.enableNonUSOrders,
  };

  if (resolvedShippingPreference === OWN) {
    return {
      type: OWN,
      params: buildOwnShippingProfileResetPayload({
        currentUser,
        enableNonUSOrders,
        phoneNumber: normalizedPhoneNumber,
      }),
    };
  }

  return {
    type: TUTULIST_LOWERCASE,
    params: {
      shippingDetails,
      otherShippingPreferences: updatedShippingPreferences,
      shippingPreference: resolvedShippingPreference,
    },
  };
};

export const submitShippingDetails = async ({
  values,
  currentUser,
  onUpdateProfile,
  onCreateShippoAddress,
}) => {
  const payload = buildShippingDetailsSubmitPayload(values, { currentUser });

  if (payload.type === OWN) {
    return onUpdateProfile(payload.params);
  }

  return onCreateShippoAddress(payload.params);
};

export const createShippingDetailsSubmitHandler = ({
  currentUser,
  onUpdateProfile,
  onCreateShippoAddress,
}) => values =>
  submitShippingDetails({
    values,
    currentUser,
    onUpdateProfile,
    onCreateShippoAddress,
  });
